'use client';

import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface DeviceWizardProps {
  onClose: () => void;
  onSuccess: () => void;
}

const steps = [
  { id: 1, label: 'Informazioni Base', icon: 'ServerIcon' },
  { id: 2, label: 'Rete e IP', icon: 'GlobeAltIcon' },
  { id: 3, label: 'Dettagli Tecnici', icon: 'WrenchScrewdriverIcon' },
  { id: 4, label: 'Riepilogo', icon: 'CheckCircleIcon' },
];

const categorie = [
  { value: 'laboratorio', label: 'Laboratorio', desc: 'Analizzatori, centrifughe, strumenti lab' },
  { value: 'ufficio', label: 'Ufficio', desc: 'PC, stampanti, scanner ufficio' },
  { value: 'medico', label: 'Medico', desc: 'Dispositivi medici certificati' },
  { value: 'iot', label: 'IoT', desc: 'Sensori, dispositivi connessi' },
  { value: 'server', label: 'Server', desc: 'Server fisici e virtuali' },
  { value: 'virtuale', label: 'Virtuale', desc: 'Macchine virtuali, container' },
  { value: 'rete', label: 'Rete', desc: 'Switch, router, access point' },
];

const stati = [
  { value: 'online', label: 'Online', color: 'text-green-400' },
  { value: 'offline', label: 'Offline', color: 'text-red-400' },
  { value: 'manutenzione', label: 'Manutenzione Programmata', color: 'text-purple-400' },
];

interface FormData {
  hostname: string;
  categoria: string;
  stato: string;
  reparto: string;
  responsabile: string;
  ip_address: string;
  mac_address: string;
  ip_type: string;
  subnet_id: string;
  vlan_id: string;
  modello: string;
  numero_seriale: string;
  firmware: string;
  note: string;
}

const initialForm: FormData = {
  hostname: '', categoria: 'ufficio', stato: 'offline',
  reparto: '', responsabile: '',
  ip_address: '', mac_address: '', ip_type: 'statico',
  subnet_id: '', vlan_id: '',
  modello: '', numero_seriale: '', firmware: '', note: '',
};

export default function DeviceWizard({ onClose, onSuccess }: DeviceWizardProps) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormData>(initialForm);
  const [subnets, setSubnets] = useState<any[]>([]);
  const [vlans, setVlans] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const supabase = createClient();
  const { user } = useAuth();

  useEffect(() => {
    const load = async () => {
      const [{ data: sn }, { data: vl }] = await Promise.all([
        supabase.from('subnets').select('id, nome, cidr').order('nome'),
        supabase.from('vlans').select('id, nome, vlan_id').order('vlan_id'),
      ]);
      setSubnets(sn || []);
      setVlans(vl || []);
    };
    load();
  }, []);

  const set = (key: keyof FormData, value: string) => {
    setForm(p => ({ ...p, [key]: value }));
    setErrors(p => { const n = { ...p }; delete n[key]; return n; });
  };

  const validateStep = (s: number): boolean => {
    const errs: Record<string, string> = {};
    if (s === 1) {
      if (!form.hostname.trim()) errs.hostname = 'Il nome host è obbligatorio';
      if (!form.categoria) errs.categoria = 'Seleziona una categoria';
    }
    if (s === 2) {
      if (form.ip_address && !/^(\d{1,3}\.){3}\d{1,3}$/.test(form.ip_address)) {
        errs.ip_address = 'Formato IP non valido (es. 192.168.1.10)';
      }
      if (form.mac_address && !/^([0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}$/.test(form.mac_address)) {
        errs.mac_address = 'Formato MAC non valido (es. 00:1A:2B:3C:4D:5E)';
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const next = () => {
    if (validateStep(step)) setStep(s => s + 1);
  };

  const prev = () => setStep(s => s - 1);

  const handleSubmit = async () => {
    if (!validateStep(step)) return;
    setSaving(true);
    try {
      const payload: any = {
        hostname: form.hostname.trim(),
        categoria: form.categoria,
        stato: form.stato,
        reparto: form.reparto.trim(),
        responsabile: form.responsabile.trim(),
        ip_address: form.ip_address.trim() || null,
        mac_address: form.mac_address.trim() || null,
        ip_type: form.ip_type,
        subnet_id: form.subnet_id || null,
        vlan_id: form.vlan_id || null,
        modello: form.modello.trim(),
        numero_seriale: form.numero_seriale.trim(),
        firmware: form.firmware.trim(),
        note: form.note.trim(),
        created_by: user?.id || null,
      };

      const { error } = await supabase.from('devices').insert(payload);
      if (error) throw error;

      // Log activity
      await supabase.from('activity_log').insert({
        tipo: 'dispositivo_aggiunto',
        messaggio: `Dispositivo "${form.hostname}" aggiunto`,
        dettaglio: `Categoria: ${form.categoria} · Reparto: ${form.reparto}`,
        ip_address: form.ip_address || null,
        user_id: user?.id || null,
      });

      onSuccess();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const Tooltip = ({ text }: { text: string }) => (
    <span className="group relative inline-flex ml-1">
      <Icon name="QuestionMarkCircleIcon" size={14} className="text-muted-color cursor-help" />
      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-surface-elevated border border-border rounded-lg px-3 py-2 text-xs text-secondary-color opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-xl">
        {text}
      </span>
    </span>
  );

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-border rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-white">Aggiungi Nuovo Dispositivo</h2>
            <p className="text-sm text-muted-color mt-0.5">Passo {step} di {steps.length}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-surface-elevated transition-colors">
            <Icon name="XMarkIcon" size={18} className="text-muted-color" />
          </button>
        </div>

        {/* Step indicators */}
        <div className="flex px-6 py-4 gap-2 border-b border-border flex-shrink-0">
          {steps.map((s) => (
            <div key={s.id} className="flex-1 flex flex-col items-center gap-1.5">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                step > s.id ? 'bg-green-500' : step === s.id ? 'bg-sky-500' : 'bg-surface-elevated border border-border'
              }`}>
                {step > s.id
                  ? <Icon name="CheckIcon" size={14} className="text-white" />
                  : <Icon name={s.icon as any} size={14} className={step === s.id ? 'text-white' : 'text-muted-color'} />
                }
              </div>
              <span className={`text-[10px] font-medium text-center leading-tight ${step === s.id ? 'text-sky-400' : step > s.id ? 'text-green-400' : 'text-muted-color'}`}>
                {s.label}
              </span>
            </div>
          ))}
        </div>

        {/* Form content */}
        <div className="flex-1 overflow-y-auto scrollbar-thin px-6 py-5">
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <label className="flex items-center text-sm font-semibold text-secondary-color mb-2">
                  Nome Host (Hostname) *
                  <Tooltip text="Il nome univoco del dispositivo in rete. Es: analizzatore-ema-01, server-db-01" />
                </label>
                <input
                  value={form.hostname}
                  onChange={e => set('hostname', e.target.value)}
                  placeholder="es. analizzatore-ema-01"
                  className={`w-full bg-surface-elevated border rounded-xl px-4 py-3 text-base text-white placeholder:text-muted-color focus:outline-none focus:ring-1 transition-colors ${errors.hostname ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-border focus:border-sky-500 focus:ring-sky-500'}`}
                />
                {errors.hostname && <p className="text-xs text-red-400 mt-1">{errors.hostname}</p>}
              </div>

              <div>
                <label className="flex items-center text-sm font-semibold text-secondary-color mb-2">
                  Categoria *
                  <Tooltip text="Il tipo di dispositivo. Scegli la categoria più adatta per organizzare l'inventario." />
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {categorie.map(c => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => set('categoria', c.value)}
                      className={`text-left px-4 py-3 rounded-xl border transition-all ${form.categoria === c.value ? 'border-sky-500 bg-sky-500/10' : 'border-border bg-surface-elevated hover:border-border/80'}`}
                    >
                      <p className={`text-sm font-semibold ${form.categoria === c.value ? 'text-sky-400' : 'text-white'}`}>{c.label}</p>
                      <p className="text-xs text-muted-color mt-0.5">{c.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-center text-sm font-semibold text-secondary-color mb-2">
                  Stato Iniziale
                  <Tooltip text="Lo stato attuale del dispositivo. Puoi modificarlo in seguito." />
                </label>
                <div className="flex gap-2">
                  {stati.map(s => (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => set('stato', s.value)}
                      className={`flex-1 py-2.5 rounded-xl border text-sm font-semibold transition-all ${form.stato === s.value ? 'border-sky-500 bg-sky-500/10 text-sky-400' : 'border-border bg-surface-elevated text-secondary-color hover:text-white'}`}
                    >
                      <span className={form.stato === s.value ? '' : s.color}>{s.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center text-sm font-semibold text-secondary-color mb-2">
                    Reparto
                    <Tooltip text="Il reparto o area dove si trova il dispositivo. Es: Laboratorio, Uffici, CED" />
                  </label>
                  <input
                    value={form.reparto}
                    onChange={e => set('reparto', e.target.value)}
                    placeholder="es. Laboratorio"
                    className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="flex items-center text-sm font-semibold text-secondary-color mb-2">
                    Responsabile
                    <Tooltip text="Il nome della persona responsabile di questo dispositivo." />
                  </label>
                  <input
                    value={form.responsabile}
                    onChange={e => set('responsabile', e.target.value)}
                    placeholder="es. Dott. Rossi"
                    className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center text-sm font-semibold text-secondary-color mb-2">
                    Indirizzo IP
                    <Tooltip text="L'indirizzo IPv4 del dispositivo. Es: 192.168.1.10 — Lascia vuoto se non ancora assegnato." />
                  </label>
                  <input
                    value={form.ip_address}
                    onChange={e => set('ip_address', e.target.value)}
                    placeholder="es. 192.168.1.10"
                    className={`w-full bg-surface-elevated border rounded-xl px-4 py-3 text-base font-mono text-white placeholder:text-muted-color focus:outline-none focus:ring-1 transition-colors ${errors.ip_address ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-border focus:border-sky-500 focus:ring-sky-500'}`}
                  />
                  {errors.ip_address && <p className="text-xs text-red-400 mt-1">{errors.ip_address}</p>}
                </div>
                <div>
                  <label className="flex items-center text-sm font-semibold text-secondary-color mb-2">
                    Indirizzo MAC
                    <Tooltip text="L'indirizzo fisico della scheda di rete. Es: 00:1A:2B:3C:4D:5E — Usato per identificare univocamente il dispositivo." />
                  </label>
                  <input
                    value={form.mac_address}
                    onChange={e => set('mac_address', e.target.value)}
                    placeholder="es. 00:1A:2B:3C:4D:5E"
                    className={`w-full bg-surface-elevated border rounded-xl px-4 py-3 text-base font-mono text-white placeholder:text-muted-color focus:outline-none focus:ring-1 transition-colors ${errors.mac_address ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-border focus:border-sky-500 focus:ring-sky-500'}`}
                  />
                  {errors.mac_address && <p className="text-xs text-red-400 mt-1">{errors.mac_address}</p>}
                </div>
              </div>

              <div>
                <label className="flex items-center text-sm font-semibold text-secondary-color mb-2">
                  Tipo di Assegnazione IP
                  <Tooltip text="Statico: IP fisso configurato manualmente. DHCP: IP assegnato automaticamente dal server." />
                </label>
                <div className="flex gap-3">
                  {[
                    { value: 'statico', label: 'Statico', desc: 'IP fisso, configurato manualmente' },
                    { value: 'dhcp', label: 'DHCP', desc: 'IP automatico dal server DHCP' },
                  ].map(t => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => set('ip_type', t.value)}
                      className={`flex-1 text-left px-4 py-3 rounded-xl border transition-all ${form.ip_type === t.value ? 'border-sky-500 bg-sky-500/10' : 'border-border bg-surface-elevated hover:border-border/80'}`}
                    >
                      <p className={`text-sm font-semibold ${form.ip_type === t.value ? 'text-sky-400' : 'text-white'}`}>{t.label}</p>
                      <p className="text-xs text-muted-color mt-0.5">{t.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center text-sm font-semibold text-secondary-color mb-2">
                    Subnet
                    <Tooltip text="La sottorete a cui appartiene il dispositivo. Definisce il range di indirizzi IP disponibili." />
                  </label>
                  <select
                    value={form.subnet_id}
                    onChange={e => set('subnet_id', e.target.value)}
                    className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white focus:outline-none focus:border-sky-500 transition-colors"
                  >
                    <option value="">Nessuna subnet</option>
                    {subnets.map(s => (
                      <option key={s.id} value={s.id}>{s.nome} ({s.cidr})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="flex items-center text-sm font-semibold text-secondary-color mb-2">
                    VLAN
                    <Tooltip text="La VLAN di appartenenza. Separa logicamente il traffico di rete per area o funzione." />
                  </label>
                  <select
                    value={form.vlan_id}
                    onChange={e => set('vlan_id', e.target.value)}
                    className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white focus:outline-none focus:border-sky-500 transition-colors"
                  >
                    <option value="">Nessuna VLAN</option>
                    {vlans.map(v => (
                      <option key={v.id} value={v.id}>VLAN {v.vlan_id} — {v.nome}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center text-sm font-semibold text-secondary-color mb-2">
                    Modello
                    <Tooltip text="Il modello commerciale del dispositivo. Es: Sysmex XN-1000, Dell PowerEdge R740" />
                  </label>
                  <input
                    value={form.modello}
                    onChange={e => set('modello', e.target.value)}
                    placeholder="es. Sysmex XN-1000"
                    className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="flex items-center text-sm font-semibold text-secondary-color mb-2">
                    Numero Seriale
                    <Tooltip text="Il numero di serie univoco del dispositivo, riportato sull'etichetta fisica." />
                  </label>
                  <input
                    value={form.numero_seriale}
                    onChange={e => set('numero_seriale', e.target.value)}
                    placeholder="es. SN-LAB-001"
                    className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base font-mono text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center text-sm font-semibold text-secondary-color mb-2">
                  Versione Firmware
                  <Tooltip text="La versione del software interno del dispositivo. Importante per la sicurezza e compatibilità." />
                </label>
                <input
                  value={form.firmware}
                  onChange={e => set('firmware', e.target.value)}
                  placeholder="es. 3.2.1"
                  className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base font-mono text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                />
              </div>

              <div>
                <label className="flex items-center text-sm font-semibold text-secondary-color mb-2">
                  Note
                  <Tooltip text="Informazioni aggiuntive sul dispositivo: posizione fisica, configurazioni speciali, avvertenze." />
                </label>
                <textarea
                  value={form.note}
                  onChange={e => set('note', e.target.value)}
                  rows={4}
                  placeholder="Inserisci note, posizione fisica, configurazioni speciali..."
                  className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors resize-none"
                />
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <div className="bg-surface-elevated border border-border rounded-xl p-5">
                <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                  <Icon name="CheckCircleIcon" size={16} className="text-green-400" />
                  Riepilogo Dispositivo
                </h3>
                <dl className="space-y-3">
                  {[
                    { label: 'Hostname', value: form.hostname },
                    { label: 'Categoria', value: form.categoria },
                    { label: 'Stato', value: form.stato },
                    { label: 'Reparto', value: form.reparto || '—' },
                    { label: 'Responsabile', value: form.responsabile || '—' },
                    { label: 'Indirizzo IP', value: form.ip_address || '—' },
                    { label: 'MAC Address', value: form.mac_address || '—' },
                    { label: 'Tipo IP', value: form.ip_type === 'statico' ? 'Statico' : 'DHCP' },
                    { label: 'Modello', value: form.modello || '—' },
                    { label: 'N. Seriale', value: form.numero_seriale || '—' },
                    { label: 'Firmware', value: form.firmware || '—' },
                  ].map(row => (
                    <div key={row.label} className="flex justify-between gap-3">
                      <dt className="text-xs text-muted-color">{row.label}</dt>
                      <dd className="text-xs font-semibold text-white text-right">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
              {form.note && (
                <div className="bg-surface-elevated border border-border rounded-xl p-4">
                  <p className="text-xs font-semibold text-muted-color mb-2">NOTE</p>
                  <p className="text-sm text-secondary-color">{form.note}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border flex-shrink-0">
          <button
            onClick={step === 1 ? onClose : prev}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-border text-sm font-semibold text-secondary-color hover:text-white hover:bg-surface-elevated transition-all"
          >
            <Icon name="ChevronLeftIcon" size={16} />
            {step === 1 ? 'Annulla' : 'Indietro'}
          </button>

          {step < 4 ? (
            <button
              onClick={next}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-white text-sm font-bold transition-all active:scale-95"
            >
              Avanti
              <Icon name="ChevronRightIcon" size={16} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-green-500 hover:bg-green-400 disabled:opacity-60 text-white text-sm font-bold transition-all active:scale-95"
            >
              {saving ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Salvataggio...
                </>
              ) : (
                <>
                  <Icon name="CheckIcon" size={16} />
                  Aggiungi Dispositivo
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
